import React, { useState, useEffect } from 'react'
import { Breadcrumbs, Typography, ThemeProvider } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UserSchedulesForm from './announceForms/announceSchedulesForm'
import UserPossibilitiesForm from './announceForms/announcePossibilitiesForm'
import UserContactForm from './announceForms/announceContactForm'
import theme from '../../themes/theme'
import { useTranslation } from 'react-i18next'
import MyButton from '../../components/buttons'
import ImageUpload from './announceForms/announcePicturesForm'
import * as Sentry from '@sentry/react'
import {
  postMyEstablishment,
  fetchEstablishmentByUserId,
  updateMyEstablishment,
} from '../../../services/announcesEstablishment'
import UserTitleForm from './announceForms/announceTitle'
import UserPricesForm from './announceForms/announcePricesForm'
import UserServicesEstablishmentForm from './announceForms/announceServicesEstablishmentForm'
import Modal from '../../components/MyModal'
import {
  validateFormData,
  ValidationError,
} from '../../validators/postAnnounce'
import { IoIosWarning } from 'react-icons/io'
import CookieManager from '../../lib/CookiesManager'
import StepSelector from '../../components/StepSelector'

interface Field {
  type: string
  label: string
  options?: string[]
}

interface Step {
  title: string
  fields: Field[]
}

const FormWizard: React.FC<{ userProfileFormProps: any }> = ({
  userProfileFormProps,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeStep, setActiveStep] = useState(() => {
    const stepParam = searchParams.get('step')
    return stepParam ? parseInt(stepParam, 10) : 0
  })
  const [formData, setFormData] = useState(() => {
    const initialData = userProfileFormProps || {}
    const myEstablishmentStr = localStorage.getItem('myEstablishment')
    const myEstablishment = myEstablishmentStr
      ? JSON.parse(myEstablishmentStr)
      : null
    return myEstablishment && myEstablishment.hasOwnProperty('title')
      ? { ...initialData, ...myEstablishment }
      : initialData
  })
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [isStepValid, setIsStepValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [, setStepValidity] = useState<boolean[]>([])
  const { t } = useTranslation()
  const [hasExistingEstablishment, setHasExistingEstablishment] =
    useState(false)
  const [popupMessage, setPopupMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState<
    'success' | 'error' | null
  >(null)

  const steps: Step[] = [
    {
      title: t('create-ad.my-services'),
      fields: [{ type: 'multiselect', label: 'status' }],
    },

    {
      title: t('create-ad.schedules'),
      fields: [],
    },
    {
      title: t('create-ad.possibilities'),
      fields: [],
    },
    {
      title: t('create-ad.contact'),
      fields: [],
    },
    {
      title: t('create-ad.prices'),
      fields: [{ type: 'input', label: 'price' }],
    },
    {
      title: t('create-ad.announce'),
      fields: [
        { type: 'input', label: 'title' },
        { type: 'textarea', label: 'description' },
      ],
    },
    {
      title: t('create-ad.pictures'),
      fields: [],
    },
  ]

  useEffect(() => {
    const currentStep = searchParams.get('step')
    if (currentStep !== activeStep.toString()) {
      setSearchParams({ step: activeStep.toString() })
    }
  }, [activeStep])

  useEffect(() => {
    const stepParam = searchParams.get('step')
    if (stepParam) {
      const step = parseInt(stepParam, 10)
      if (step >= 0 && step < steps.length) {
        setActiveStep(step)
      }
    }
  }, [searchParams])

  useEffect(() => {
    const validateCurrentStep = () => {
      const isValid = steps[activeStep].fields.every((field: any) => {
        const fieldValue = formData[field.label]
        if (field.label === 'weight') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 40 &&
            fieldValue <= 120
          )
        } else if (field.label === 'height') {
          return (
            typeof fieldValue === 'number' &&
            fieldValue >= 140 &&
            fieldValue <= 220
          )
        } else if (field.label === 'nickname') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 3
        } else if (field.label === 'title') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 4
        } else if (field.label === 'description') {
          return (
            typeof fieldValue === 'string' && fieldValue.trim().length > 129
          )
        } else if (field.label === 'languages') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (field.label === 'nationality') {
          return typeof fieldValue === 'string' && fieldValue.trim().length > 0
        } else if (field.label === 'status') {
          return Array.isArray(fieldValue) && fieldValue.length > 0
        } else if (
          field.label === 'available247' ||
          field.label === 'customHours'
        ) {
          return formData['available247'] || formData['customHours']
        } else {
          return fieldValue === true || fieldValue === false
            ? true
            : !!fieldValue
        }
      })
      setIsStepValid(isValid)
      setStepValidity(prev => {
        const newValidity = [...prev]
        newValidity[activeStep] = isValid
        return newValidity
      })
    }

    validateCurrentStep()
  }, [activeStep, formData])

  useEffect(() => {
    const myEstablishment = JSON.parse(
      localStorage.getItem('myEstablishment') || '{}',
    )
    if (!myEstablishment.hasOwnProperty('status')) {
      localStorage.setItem('myEstablishment', JSON.stringify(myEstablishment))
    }
    const checkLocalStorage = () => {
      const myEstablishmentStr = localStorage.getItem('myEstablishment')
      if (myEstablishmentStr) {
        const myEstablishment = JSON.parse(myEstablishmentStr)
        setFormData((prev: any) => ({
          ...prev,
          ...myEstablishment,
        }))
      }
    }

    const fetchExistingEstablishment = async () => {
      const token = CookieManager.getToken()
      const userId = CookieManager.getUserId()
      if (token && userId) {
        try {
          const myEstablishment = await fetchEstablishmentByUserId(
            userId,
            token,
          )
          if (myEstablishment) {
            setHasExistingEstablishment(true)
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              action: 'fetchExistingEstablishment',
              userId,
              timestamp: new Date().toISOString(),
            },
            tags: {
              type: 'api_error',
              method: 'fetchExistingEstablishment',
            },
          })
        }
      }
    }

    const intervalId = setInterval(checkLocalStorage, 100)
    checkLocalStorage()
    fetchExistingEstablishment()
    return () => clearInterval(intervalId)
  }, [])

  const handleBreadcrumbClick = (index: number) => {
    if (activeStep < index && !isStepValid) {
      return
    }
    setActiveStep(index)
  }

  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setActiveStep(prev => prev - 1)
  }

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setActiveStep(prev => prev + 1)
  }
  const handleSubmit = async () => {
    event?.preventDefault()
    const token = CookieManager.getToken()
    const userId = CookieManager.getUserId()

    const errors = validateFormData(formData, t, false)
    if (errors.length > 0) {
      setValidationErrors(errors)
      setShowValidationErrors(true)
      setSubmissionStatus('error')
      setIsModalOpen(true)
      return
    }

    const locationValue = formData.location ?? null

    const dataToSend = {
      ...formData,
      userId: parseInt(userId as string),
      boostedTime: new Date().toISOString(),
      practices: formData.practices || [],
      isAvailable: formData.isAvailable || false,
      location: locationValue,
      smsOnly: formData.smsOnly || false,
      callOnly: formData.callOnly || false,
      whatsapp: formData.whatsapp || false,
      emailOnly: formData.emailOnly || false,
      disponibility: formData.disponibility || [],
      availabilityStart: formData.availabilityStart || [],
      availabilityEnd: formData.availabilityEnd || [],
    }

    if (!token || !userId) {
      console.error('No token or userId found in local storage')
      return
    }

    try {
      let updatedData

      if (hasExistingEstablishment) {
        updatedData = await updateMyEstablishment(dataToSend, token, userId)
        setSubmissionStatus('success')
        setPopupMessage(t('create-ad.modify-success'))
      } else {
        updatedData = await postMyEstablishment(dataToSend, token, userId)
        setSubmissionStatus('success')
        setPopupMessage(t('create-ad.post-success'))
      }

      if (updatedData) {
        localStorage.setItem('myEstablishment', JSON.stringify(updatedData))
        setFormData(updatedData)
      }
    } catch (error: any) {
      console.error('Error details:', error)
      Sentry.captureException(error, {
        extra: {
          action: 'handleSubmit',
          userId,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'handleSubmit',
        },
      })
      setSubmissionStatus('error')
      setPopupMessage(error.message || t('create-ad.post-error'))
    } finally {
      setIsModalOpen(true)
    }
  }

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    if (submissionStatus === 'success') {
      setSearchParams({})
      navigate('/dashboard/announces-establishment/')
    }
    window.location.reload()
  }
  const renderValidationErrors = () => {
    if (!showValidationErrors || validationErrors.length === 0) return null

    return (
      <div className='bg-redlight p-4 rounded-lg mb-4'>
        <ul className='space-y-2'>
          {validationErrors.map((error, index) => (
            <li
              key={index}
              className='text-anthracite text-left flex items-start'
            >
              <IoIosWarning
                className='text-redpink flex-shrink-0 mt-1 mr-2'
                size={16}
              />
              <span>
                <b>{error.stepName} :</b> {error.message}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderBreadcrumbs = () => {
    if (isMobile) {
      return (
        <StepSelector
          steps={steps}
          activeStep={activeStep}
          onStepSelect={handleBreadcrumbClick}
          isStepValid={isStepValid}
        />
      )
    }

    return (
      <div className='w-full overflow-hidden mb-2'>
        <div className='overflow-x-auto md:overflow-visible '>
          <Breadcrumbs
            aria-label='breadcrumb'
            separator=''
            className='bg-anthracite rounded-lg min-w-fit'
          >
            <ol className='list-none flex w-full bg-white rounded-lg whitespace-nowrap'>
              {steps.map((step, index) => (
                <li
                  key={step.title}
                  className={`breadcrumb-item mr-[-18px] text-[11px] sm:text-xs pr-2 sm:pr-4 md:pr-6 items-center text-center  cursor-pointer py-2 md:py-3 text-xs md:text-sm ${
                    activeStep >= index
                      ? 'bg-redpink text-white'
                      : 'bg-anthracite text-white'
                  }`}
                  style={{
                    clipPath:
                      index !== 0
                        ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)'
                        : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 0px 50%)',
                    borderRadius:
                      index === 0
                        ? '8px 0 0 8px'
                        : index === steps.length - 1
                        ? '0 8px 8px 0'
                        : 'none',
                    paddingLeft: index === 0 ? '16px' : '32px',
                  }}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  <Typography>
                    <span className='mr-1 md:mr-2 font-bold'>{index + 1}.</span>
                    {step.title}
                  </Typography>
                </li>
              ))}
            </ol>
          </Breadcrumbs>
        </div>
      </div>
    )
  }

  return (
    <div className='w-full mb-20'>
      {renderBreadcrumbs()}
      {renderValidationErrors()}
      <ThemeProvider theme={theme}>
        <form className='mt-5 mb-5'>
          {activeStep === 0 && (
            <UserServicesEstablishmentForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}

          {activeStep === 1 && (
            <UserSchedulesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 2 && (
            <UserPossibilitiesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 3 && (
            <UserContactForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 4 && (
            <UserPricesForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 5 && (
            <UserTitleForm
              formData={formData}
              handleFieldChange={handleFieldChange}
            />
          )}
          {activeStep === 6 && <ImageUpload />}
          <div className='fixed bottom-0 left-0 w-full bg-white p-2 shadow-lg sm:shadow-none sm:static sm:p-0 z-40'>
            <div
              className={`flex flex-row space-x-2 w-full mt-2 mx-auto ${
                activeStep === 0 ? 'justify-end' : 'justify-between'
              }`}
            >
              {activeStep !== 0 && (
                <MyButton
                  className='w-full sm:w-auto'
                  variant='redpink-anthracite'
                  onClick={handleBack}
                >
                  {t('create-ad.previous')}
                </MyButton>
              )}
              {activeStep === steps.length - 1 ? (
                <>
                  {hasExistingEstablishment ? (
                    <MyButton
                      className='w-full sm:w-auto'
                      variant='green'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div className='flex items-center justify-center gap-2'>
                          <svg
                            className='animate-spin h-5 w-5 text-white'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          {t('saving')}
                        </div>
                      ) : (
                        t('create-ad.modify-ad')
                      )}
                    </MyButton>
                  ) : (
                    <MyButton
                      className='w-full sm:w-auto'
                      variant={isStepValid ? 'green' : 'redpink'}
                      disabled={!isStepValid}
                      onClick={handleSubmit}
                    >
                      {t('create-ad.post-ad')}
                    </MyButton>
                  )}
                </>
              ) : (
                <MyButton
                  className='w-full sm:w-auto'
                  variant={isStepValid ? 'redpink-anthracite' : 'redpink'}
                  disabled={!isStepValid}
                  onClick={handleNext}
                >
                  {t('create-ad.next')}
                </MyButton>
              )}
            </div>
          </div>
        </form>
      </ThemeProvider>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('announce-status')}
          </h2>
          <div className='space-y-4 p-5'>
            {submissionStatus === 'error' && validationErrors.length > 0 ? (
              <div>
                <p className='text-anthracite bg-grisclair rounded-lg py-2 mb-2'>
                  {t('create-ad.form-validation-failed')}
                </p>
                {renderValidationErrors()}
              </div>
            ) : (
              <p
                className={`text-center rounded-lg p-2 mb-4 ${
                  submissionStatus === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-redlight text-red-800'
                }`}
              >
                {submissionStatus === 'success'
                  ? t('announce-successfully-updated')
                  : t('announce-error-updated')}
              </p>
            )}

            <div className='flex justify-end'>
              <button
                onClick={handleCloseModal}
                className='bg-anthracite text-white px-4 py-2 rounded hover:bg-opacity-90'
              >
                {submissionStatus === 'error' && validationErrors.length > 0
                  ? t('create-ad.correct-errors')
                  : t('close')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default FormWizard
